import React, { useState } from "react";
import {
  Badge,
  Button,
  Checkbox,
  Group,
  Modal,
  ScrollArea,
  Stack,
  Table,
  Text,
} from "@mantine/core";
import {
  usePendingBalanceImports,
  useReviewBalanceImports,
} from "api/leaveReport/balanceImport";

interface Props {
  opened: boolean;
  onClose: () => void;
}

export function PendingBalanceReviewModal({ opened, onClose }: Props) {
  const { data, isLoading } = usePendingBalanceImports(opened);
  const review = useReviewBalanceImports();
  const [selected, setSelected] = useState<string[]>([]);

  const items = data?.items ?? [];

  const toggle = (id: string) =>
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  const toggleAll = () =>
    setSelected(selected.length === items.length ? [] : items.map((i) => i._id));

  const act = async (action: "approve" | "ignore") => {
    if (selected.length === 0) return;
    await review.mutateAsync({ ids: selected, action });
    setSelected([]);
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Pending Balance Imports"
      size="xl"
      centered
    >
      <Stack spacing="md">
        <Text size="sm" color="dimmed">
          Approving (Keep) sets the employee&apos;s allocated balance for that
          leave type and fiscal year to the proposed value (balance is
          recomputed). Ignoring discards the staged row.
        </Text>

        {(() => {
          if (isLoading) return <Text color="dimmed">Loading…</Text>;
          if (items.length === 0) return <Text color="dimmed">No pending balance imports.</Text>;
          return (
            <>
              <ScrollArea h={380}>
                <Table striped highlightOnHover fontSize="xs">
                  <thead>
                    <tr>
                      <th>
                        <Checkbox
                          checked={selected.length === items.length}
                          indeterminate={
                          selected.length > 0 && selected.length < items.length
                        }
                          onChange={toggleAll}
                          aria-label="Select all"
                        />
                      </th>
                      <th>Email</th>
                      <th>Leave Type</th>
                      <th>Current</th>
                      <th>Proposed</th>
                      <th>Fiscal Year</th>
                      <th>Effective Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((it) => (
                      <tr key={it._id}>
                        <td>
                          <Checkbox
                            checked={selected.includes(it._id)}
                            onChange={() => toggle(it._id)}
                            aria-label={`Select ${it.email}`}
                          />
                        </td>
                        <td>{it.email}</td>
                        <td>{it.leaveType}</td>
                        <td>
                          {it.currentAllocated} <Text span color="dimmed">/ {it.currentBalance}</Text>
                        </td>
                        <td>
                          <Badge color="blue" variant="light">
                            {it.balanceCount}
                          </Badge>
                        </td>
                        <td>{it.fiscalYear}</td>
                        <td>{String(it.effectiveDate).slice(0, 10)}</td>
                      </tr>
                  ))}
                  </tbody>
                </Table>
              </ScrollArea>

              <Group position="apart">
                <Text size="sm" color="dimmed">
                  {selected.length} selected
                </Text>
                <Group>
                  <Button
                    variant="light"
                    color="red"
                    disabled={selected.length === 0}
                    loading={review.isLoading}
                    onClick={() => act("ignore")}
                  >
                    Ignore
                  </Button>
                  <Button
                    disabled={selected.length === 0}
                    loading={review.isLoading}
                    onClick={() => act("approve")}
                  >
                    Keep (Approve)
                  </Button>
                </Group>
              </Group>
            </>
          );
        })()}
      </Stack>
    </Modal>
  );
}

export default PendingBalanceReviewModal;
